import type { RequestDocument } from 'graphql-request';

/**
 * GraphQL reads (CDA, camelCase) for the `publish_record` model. Paired with
 * the CMA writes in `src/repositories/PublishRecordRepository.ts`. The JSON
 * fields (`attempts`, `outputJson`, `snapshotRef`, `fingerprintsJson`) come back
 * already parsed on the CDA path. `videoId` / `storyId` are plain string ids
 * (not Dato links), queried with `{ in }` / `{ eq }`.
 */
const PUBLISH_RECORD_FIELDS = `
  id
  videoId
  storyId
  groupId
  mirrorsToLegacy
  language
  aspectRatio
  resolution
  publishStatus
  renderId
  attempts
  errorCode
  errorMessage
  renderStartedAt
  renderCompletedAt
  outputJson
  snapshotRef
  fingerprintsJson
  publishedBy
  publishedOn
  renderSourceKey
`;

/**
 * All publish records for a batch of Video records (the clip's main + associated
 * per-AR ids), newest first. Powers the Downloads panel; the repository
 * paginates and the store/service derives latest-per-language and groups by
 * `groupId` for history.
 */
export const PUBLISH_RECORDS_FOR_VIDEOS_QUERY: RequestDocument = `
  query getPublishRecordsForVideos($videoIds: [String], $first: IntType!, $skip: IntType!) {
    allPublishRecords(
      filter: { videoId: { in: $videoIds } }
      orderBy: _createdAt_DESC
      first: $first
      skip: $skip
    ) {
      ${PUBLISH_RECORD_FIELDS}
    }
  }
`;

/**
 * In-flight publish records for one story (seed for live tracking on load /
 * refresh). The status filter is correct here because it is a one-time query;
 * the live subscription watches the resulting id set instead (a record that
 * finishes must not drop out of the watched set before its final push).
 */
export const IN_FLIGHT_PUBLISH_RECORDS_QUERY: RequestDocument = `
  query getInFlightPublishRecords($storyId: String, $statuses: [String], $first: IntType!, $skip: IntType!) {
    allPublishRecords(
      filter: { storyId: { eq: $storyId }, publishStatus: { in: $statuses } }
      orderBy: _createdAt_DESC
      first: $first
      skip: $skip
    ) {
      ${PUBLISH_RECORD_FIELDS}
    }
  }
`;

/**
 * Only the fields that change over a render's lifecycle. The live subscription
 * selects just these and merges them onto the full record cached at
 * seed/dispatch — immutable coordinates (video/story/language/AR/resolution/
 * snapshotRef/…) don't need to ride every push.
 */
const PUBLISH_RECORD_STATUS_FIELDS = `
  id
  publishStatus
  renderId
  renderSourceKey
  renderStartedAt
  renderCompletedAt
  errorMessage
  errorCode
  attempts
  outputJson
`;

/**
 * Live status of a fixed set of publish records, by id — the query the real-time
 * subscription runs. Watch the in-flight id set and prune each id on its
 * finishing push; do NOT use the status filter here, or a record that finishes
 * would drop out of the result set and its final `succeeded`/`outputJson` push
 * would be missed.
 */
export const PUBLISH_RECORDS_STATUS_QUERY: RequestDocument = `
  query getPublishRecordsStatus($ids: [ItemId]) {
    allPublishRecords(filter: { id: { in: $ids } }) {
      ${PUBLISH_RECORD_STATUS_FIELDS}
    }
  }
`;
